import { useState, useEffect } from "react";
import { useAdmin, UserSubscription } from "@/hooks/useAdmin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, CreditCard, Palette, TrendingUp, Activity } from "lucide-react";

export default function AdminDashboardPage() {
  const { getUserSubscriptions, getAuditLogs } = useAdmin();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalSubscriptions: 0,
    pendingPayments: 0,
    recentActivity: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      // In a real implementation, these would be actual API calls
      // For now, we'll use mock data
      setStats({
        totalUsers: 1247,
        activeUsers: 892,
        totalSubscriptions: 756,
        pendingPayments: 23,
        recentActivity: 142
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard Administrativo</h1>
        <p className="text-muted-foreground">
          Visão geral do sistema e métricas importantes.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Usuários</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalUsers.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              +12% em relação ao mês passado
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Usuários Ativos</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeUsers.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              +8% em relação ao mês passado
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Assinaturas</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalSubscriptions.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              +15% em relação ao mês passado
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pagamentos Pendentes</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingPayments}</div>
            <p className="text-xs text-muted-foreground">
              -5% em relação ao mês passado
            </p>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h3 className="font-medium">Verificar Pagamentos</h3>
                <p className="text-sm text-muted-foreground">
                  {stats.pendingPayments} pagamentos pendentes
                </p>
              </div>
              <Badge variant="secondary" className="px-3 py-1">
                Requer atenção
              </Badge>
            </div>
            
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h3 className="font-medium">Gerenciar Usuários</h3>
                <p className="text-sm text-muted-foreground">
                  {stats.totalUsers} usuários cadastrados
                </p>
              </div>
              <Badge variant="outline" className="px-3 py-1">
                Gerenciar
              </Badge>
            </div>
            
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h3 className="font-medium">Configurar Temas</h3>
                <p className="text-sm text-muted-foreground">
                  Personalizar aparência do sistema
                </p>
              </div>
              <Badge variant="outline" className="px-3 py-1">
                Configurar
              </Badge>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Atividade Recente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center space-x-4 pb-2 border-b">
                <Badge variant="outline">Admin</Badge>
                <span className="text-sm">Sistema atualizado</span>
                <span className="text-xs text-muted-foreground ml-auto">2 horas atrás</span>
              </div>
              
              <div className="flex items-center space-x-4 pb-2 border-b">
                <Badge variant="outline">Pagamento</Badge>
                <span className="text-sm">Novo pagamento verificado</span>
                <span className="text-xs text-muted-foreground ml-auto">5 horas atrás</span>
              </div>
              
              <div className="flex items-center space-x-4 pb-2">
                <Badge variant="outline">Usuário</Badge>
                <span className="text-sm">Novo usuário cadastrado</span>
                <span className="text-xs text-muted-foreground ml-auto">1 dia atrás</span>
              </div>
              
              <div className="flex items-center space-x-4">
                <Badge variant="outline">Tema</Badge>
                <span className="text-sm">Paleta de cores atualizada</span>
                <span className="text-xs text-muted-foreground ml-auto">3 dias atrás</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
